import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Chip,
  Grid,
  Card,
  CardContent,
  CircularProgress,
  Alert,
  Divider,
  Button,
  IconButton,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Tooltip
} from '@mui/material';
import { styled } from '@mui/material/styles';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import EditIcon from '@mui/icons-material/Edit';
import CalendarTodayIcon from '@mui/icons-material/CalendarToday';
import HomeIcon from '@mui/icons-material/Home';
import PersonIcon from '@mui/icons-material/Person';
import AttachMoneyIcon from '@mui/icons-material/AttachMoney';
import DescriptionIcon from '@mui/icons-material/Description';
import ReceiptIcon from '@mui/icons-material/Receipt';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import PaymentIcon from '@mui/icons-material/Payment';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import ShareIcon from '@mui/icons-material/Share';
import EmailIcon from '@mui/icons-material/Email';
import VisibilityIcon from '@mui/icons-material/Visibility';
import DownloadIcon from '@mui/icons-material/Download';
import { saveAs } from 'file-saver';
import { fillLeasePdf } from 'src/helpers/leaseContract-helper';
import { generateReceiptGroupPdf } from 'src/helpers/receiptGroup-helper';
import LoginIcon from '@mui/icons-material/Login';
import LogoutIcon from '@mui/icons-material/Logout';
import DevicesIcon from '@mui/icons-material/Devices';
import { DataService } from 'src/services/data-service';
import SecurityDepositDialog from './SecurityDepositDialog';
import ManualPaymentDialog from './ManualPaymentDialog';
import UpdateInstallmentTotalDialog from './UpdateInstallmentTotalDialog';
import PrintOptionsDialog from 'src/pages/Receipts/PrintOptionsDialog';

interface Lease {
  id: string;
  createdDate: string;
  startDate: string;
  endDate: string;
  checkinDate?: string;
  checkoutDate?: string;
  status: string;
  refNumber: string;
  leaseType: string;
  leaseTypeId: string;
  currency: string;
  currencyId: string;
  paymentDateOn: number;
  unit: {
    id: string;
    number: string;
    rooms: number;
    bathrooms: number;
    floor: number;
    areaM2: number;
    propertyId: string;
    property?: {
      id: string;
      code: string;
      contact: string;
      propertyTypeId: string;
      propertyType: string;
      isCommercial: boolean;
      active: boolean;
    };
  };
  tenant: {
    id: string;
    fullName: string;
    phoneNumbers: string[];
    emails: string[];
    nationality: string;
    addressNumber?: string;
    addressStreet?: string;
    addressNeighborhood?: string;
    addressMunicipality?: string;
    addressCity?: string;
    identificationType: string;
  };
  fees: Array<{
    id: string;
    value: number;
    type: string;
    currency: string;
    currencyId: string;
    billingCycle: string;
    notes: string | null;
    active: boolean;
  }>;
  active: boolean;
}

interface Payment {
  receiptId: string;
  receiptReference: string;
  receiptGroupId: string;
  receiptGroupReference: string;
  amountPaid: number;
  paidAt: string;
  paymentMethod: string;
  paymentType: string;
  currency: string;
}

interface Installment {
  id: string;
  issuedAt: string;
  dueDate: string;
  status: string;
  total: number;
  paidAmount: number;
  remainingAmount: number;
  currency: string | null;
  fee: {
    value: number;
    feeTypeId: string;
    type: string | null;
    currencyId: string;
    currency: string | null;
    billingCycle: string;
    notes: string;
    id: string;
    active: boolean;
  };
  lease: any;
  payments: Payment[] | null;
  active: boolean;
}

interface SecurityDeposit {
  id: string;
  leaseId?: string;
  refNumber: string;
  amount: number;
  returnAmount: number;
  notes: string | null;
  status: string;
  paymentDate: string;
  returnDate: string;
  createdAt?: string;
  active: boolean;
}

interface ApplianceCondition {
  applianceId: string;
  conditionStatus: string;
  notes: string;
}

interface AppliancesConditions {
  checkinAppliancesConditions: ApplianceCondition[];
  checkoutAppliancesConditions: ApplianceCondition[];
}

interface ApplianceType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

interface FeeType {
  id: string;
  name: string;
  description: string;
  active: boolean;
}

const LeaseStatus: Record<
  string,
  { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }
> = {
  Draft: { label: 'Draft', color: 'warning' },
  Active: { label: 'Active', color: 'success' },
  Expired: { label: 'Expired', color: 'error' },
  Terminated: { label: 'Terminated', color: 'default' }
};

const InstallmentStatus: Record<
  string,
  { label: string; color: 'warning' | 'success' | 'error' | 'default' | 'info' }
> = {
  New: { label: 'New', color: 'info' },
  Paid: { label: 'Paid', color: 'success' },
  PartiallyPaid: { label: 'Partially Paid', color: 'warning' },
  Overdue: { label: 'Overdue', color: 'error' },
  Cancelled: { label: 'Cancelled', color: 'default' }
};

const PageHeader = styled(Box)(({ theme }) => ({
  background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
  color: 'white',
  padding: theme.spacing(1.5),
  borderRadius: theme.spacing(1.5),
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[4]
}));

const SectionCard = styled(Card)(({ theme }) => ({
  marginBottom: theme.spacing(1.5),
  boxShadow: theme.shadows[2],
  '&:hover': {
    boxShadow: theme.shadows[4]
  }
}));

enum PaymentMethod {
  Cash = 0,
  CreditCard = 1,
  DebitCard = 2,
  BankTransfer = 3,
  Check = 4,
  MobilePayment = 5,
  Other = 6
}

const PaymentMethodLabels: Record<PaymentMethod, string> = {
  [PaymentMethod.Cash]: 'Cash',
  [PaymentMethod.CreditCard]: 'Credit Card',
  [PaymentMethod.DebitCard]: 'Debit Card',
  [PaymentMethod.BankTransfer]: 'Bank Transfer',
  [PaymentMethod.Check]: 'Check',
  [PaymentMethod.MobilePayment]: 'Mobile Payment',
  [PaymentMethod.Other]: 'Other'
};

interface PaymentFormData {
  installmentId: string;
  currencyId: string;
  amount: number;
  paidAt: string;
  method: PaymentMethod;
}

const LeaseDetailsPage: React.FC = () => {
  const { leaseId } = useParams<{ leaseId: string }>();
  const navigate = useNavigate();
  const [lease, setLease] = useState<Lease | null>(null);
  const [installments, setInstallments] = useState<Installment[]>([]);
  const [securityDeposits, setSecurityDeposits] = useState<SecurityDeposit[]>(
    []
  );
  const [loading, setLoading] = useState(true);
  const [installmentsLoading, setInstallmentsLoading] = useState(false);
  const [securityDepositsLoading, setSecurityDepositsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [appliancesConditions, setAppliancesConditions] =
    useState<AppliancesConditions | null>(null);
  const [applianceTypes, setApplianceTypes] = useState<ApplianceType[]>([]);
  const [appliancesLoading, setAppliancesLoading] = useState(false);
  const [feeTypes, setFeeTypes] = useState<FeeType[]>([]);
  const [paymentDialogOpen, setPaymentDialogOpen] = useState(false);
  const [selectedInstallment, setSelectedInstallment] =
    useState<Installment | null>(null);
  const [paymentForm, setPaymentForm] = useState<PaymentFormData>({
    installmentId: '',
    currencyId: '',
    amount: 0,
    paidAt: new Date().toISOString().slice(0, 16),
    method: PaymentMethod.Cash
  });
  const [paymentLoading, setPaymentLoading] = useState(false);
  const [paymentError, setPaymentError] = useState<string | null>(null);

  // Security deposit dialog state
  const [securityDepositDialogOpen, setSecurityDepositDialogOpen] =
    useState(false);

  // Manual payment dialog state
  const [manualPaymentDialogOpen, setManualPaymentDialogOpen] = useState(false);

  // Print options dialog state
  const [printOptionsOpen, setPrintOptionsOpen] = useState(false);
  const [receiptGroupRefForPrint, setReceiptGroupRefForPrint] = useState<string | null>(null);

  // Update installment total dialog state
  const [updateTotalDialogOpen, setUpdateTotalDialogOpen] = useState(false);
  const [selectedInstallmentForUpdate, setSelectedInstallmentForUpdate] =
    useState<Installment | null>(null);

  // Repay security deposit dialog state
  const [repayDialogOpen, setRepayDialogOpen] = useState(false);
  const [selectedDeposit, setSelectedDeposit] =
    useState<SecurityDeposit | null>(null);
  const [repayForm, setRepayForm] = useState({
    returnAmount: 0,
    notes: ''
  });
  const [repayLoading, setRepayLoading] = useState(false);
  const [repayError, setRepayError] = useState<string | null>(null);

  const [pdfUrl, setPdfUrl] = useState<string | null>(null);
  const [previewOpen, setPreviewOpen] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);
  const [shareDialogOpen, setShareDialogOpen] = useState(false);

  // Notice period dialog state
  const [noticePeriodDialogOpen, setNoticePeriodDialogOpen] = useState(false);
  const [noticePeriod, setNoticePeriod] = useState<1 | 3>(1);
  const [contractAction, setContractAction] = useState<'preview' | 'download' | 'print' | 'open' | null>(null);

  useEffect(() => {
    if (leaseId) {
      fetchLeaseDetails();
      fetchLeaseInstallments();
      fetchSecurityDeposits();
      fetchAppliancesConditions();
      fetchApplianceTypes();
      fetchFeeTypes();
    }
  }, [leaseId]);

  useEffect(() => {
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [pdfUrl]);

  const fetchLeaseDetails = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await DataService.get(`api/Lease/get/${leaseId}`);

      if (response.ok) {
        const responseData = await response.json();
        let leaseData: Lease;
        if (responseData.success && responseData.data) {
          leaseData = responseData.data;
        } else if (responseData.id) {
          leaseData = responseData;
        } else {
          throw new Error('Invalid response structure');
        }
        setLease(leaseData);
      } else {
        throw new Error(
          `Failed to fetch lease: ${response.status} ${response.statusText}`
        );
      }
    } catch (error) {
      console.error('Error fetching lease:', error);
      const errorMessage =
        error instanceof Error
          ? error.message
          : 'Failed to load lease details. Please try again.';
      setError(errorMessage);
    } finally {
      setLoading(false);
    }
  };

  const fetchLeaseInstallments = async () => {
    setInstallmentsLoading(true);
    try {
      const response = await DataService.get(
        `api/Installment/lease/${leaseId}`
      );

      if (response.ok) {
        const responseData = await response.json();
        let installmentsList: Installment[] = [];

        if (responseData.success && responseData.data) {
          installmentsList = responseData.data;
        } else if (Array.isArray(responseData)) {
          installmentsList = responseData;
        }

        // Sort installments by due date
        installmentsList.sort(
          (a, b) =>
            new Date(a.dueDate).getTime() - new Date(b.dueDate).getTime()
        );

        setInstallments(installmentsList);
      } else {
        throw new Error('Failed to fetch installments');
      }
    } catch (error) {
      console.error('Error fetching installments:', error);
      setInstallments([]);
    } finally {
      setInstallmentsLoading(false);
    }
  };

  const fetchSecurityDeposits = async () => {
    setSecurityDepositsLoading(true);
    try {
      const response = await DataService.get(
        `api/SecurityDeposit/lease/${leaseId}`
      );

      if (response.ok) {
        const responseData = await response.json();
        console.log('Security Deposits Response:', responseData);
        let depositsList: SecurityDeposit[] = [];

        if (responseData.success && responseData.data) {
          // Check if data is an object (single deposit) or array
          if (Array.isArray(responseData.data)) {
            depositsList = responseData.data;
          } else if (
            typeof responseData.data === 'object' &&
            responseData.data !== null
          ) {
            // Single deposit object, wrap it in an array
            depositsList = [responseData.data];
          }
        } else if (Array.isArray(responseData)) {
          depositsList = responseData;
        } else if (
          typeof responseData === 'object' &&
          responseData !== null &&
          responseData.id
        ) {
          // Single deposit object without wrapper
          depositsList = [responseData];
        }

        // Sort by payment date (most recent first)
        depositsList.sort(
          (a, b) =>
            new Date(b.paymentDate).getTime() -
            new Date(a.paymentDate).getTime()
        );

        setSecurityDeposits(depositsList);
      } else {
        throw new Error('Failed to fetch security deposits');
      }
    } catch (error) {
      console.error('Error fetching security deposits:', error);
      setSecurityDeposits([]);
    } finally {
      setSecurityDepositsLoading(false);
    }
  };

  function getMonthlyRent(lease: Lease): number {
  if (!lease?.fees?.length) return 0;

  const rentByType = lease.fees.find(f => (f.active !== false) && (f.type?.toLowerCase() === 'rent' || f.type?.toLowerCase() === 'loyer'));
  if (rentByType) return rentByType.value;

  const rentMonthly = lease.fees.find(f => (f.active !== false) && f.billingCycle?.toLowerCase() === 'monthly');
  if (rentMonthly) return rentMonthly.value;

  return lease.fees[0].value ?? 0;
}

function toISODateOnly(d: string): string {
  try {
    const dt = new Date(d);
    const y = dt.getFullYear();
    const m = String(dt.getMonth() + 1).padStart(2, '0');
    const dd = String(dt.getDate()).padStart(2, '0');
    return `${y}-${m}-${dd}`;
  } catch {
    return (d?.length >= 10) ? d.slice(0, 10) : '2000-01-01';
  }
}

async function generatePdfUrlFromLease(l: Lease, noticeP: 1 | 3) {
  const tenantName = l.tenant?.fullName ?? '';
  const email = l.tenant?.emails?.[0] ?? '';
  const phone = l.tenant?.phoneNumbers?.[0] ?? '';
  const apartmentNumber = l.unit?.number ?? '';
  const startDateISO = toISODateOnly(l.startDate);
  const monthlyRent = getMonthlyRent(l);
  const isCommercial = l.unit?.property?.isCommercial ?? false;

  const { blobUrl } = await fillLeasePdf({
    tenantName,
    email,
    phone,
    apartmentNumber,
    startDateISO,
    monthlyRent,
    noticePeriod: noticeP,
    isCommercial,
    tenantAddress: {
      number: l.tenant?.addressNumber,
      street: l.tenant?.addressStreet,
      neighborhood: l.tenant?.addressNeighborhood,
      municipality: l.tenant?.addressMunicipality,
      city: l.tenant?.addressCity
    },
    propertyCode: l.unit?.property?.code,
    propertyContact: l.unit?.property?.contact
  });

  return blobUrl;
}

async function handlePreviewContract() {
  if (!lease) return;
  // Open notice period dialog first
  setContractAction('preview');
  setNoticePeriodDialogOpen(true);
}

async function executePreviewContract() {
  if (!lease) return;

  setPdfGenerating(true);
  setError(null);

  try {
    // Revoke old URL if exists
    if (pdfUrl) {
      URL.revokeObjectURL(pdfUrl);
      setPdfUrl(null);
    }

    const url = await generatePdfUrlFromLease(lease, noticePeriod);
    setPdfUrl(url);
    setPreviewOpen(true);
  } catch (error) {
    console.error('Error generating PDF preview:', error);
    setError(error instanceof Error ? error.message : 'Failed to generate PDF preview. Please try again.');
  } finally {
    setPdfGenerating(false);
  }
}

async function handleDownloadContract() {
  if (!lease) return;
  // Open notice period dialog first
  setContractAction('download');
  setNoticePeriodDialogOpen(true);
}

async function executeDownloadContract() {
  if (!lease) return;

  setPdfGenerating(true);
  setError(null);

  try {
    const urlData = await fillLeasePdf({
      tenantName: lease.tenant?.fullName ?? '',
      email: lease.tenant?.emails?.[0] ?? '',
      phone: lease.tenant?.phoneNumbers?.[0] ?? '',
      apartmentNumber: lease.unit?.number ?? '',
      startDateISO: toISODateOnly(lease.startDate),
      monthlyRent: getMonthlyRent(lease),
      noticePeriod: noticePeriod,
      isCommercial: lease.unit?.property?.isCommercial ?? false,
      tenantAddress: {
        number: lease.tenant?.addressNumber,
        street: lease.tenant?.addressStreet,
        neighborhood: lease.tenant?.addressNeighborhood,
        municipality: lease.tenant?.addressMunicipality,
        city: lease.tenant?.addressCity
      },
      propertyCode: lease.unit?.property?.code,
      propertyContact: lease.unit?.property?.contact
    });
    const filename = `${lease.tenant?.fullName || 'Lease'}-${lease.refNumber || lease.id}.pdf`;
    saveAs(urlData.blob, filename);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    setError(error instanceof Error ? error.message : 'Failed to download PDF. Please try again.');
  } finally {
    setPdfGenerating(false);
  }
}

async function handleOpenInNewTab() {
  if (!lease) return;
  // Open notice period dialog first
  setContractAction('open');
  setNoticePeriodDialogOpen(true);
}

async function executeOpenInNewTab() {
  if (!lease) return;

  setPdfGenerating(true);
  setError(null);

  try {
    const url = await generatePdfUrlFromLease(lease, noticePeriod);
    window.open(url, '_blank');
  } catch (error) {
    console.error('Error opening PDF:', error);
    setError(error instanceof Error ? error.message : 'Failed to open PDF. Please try again.');
  } finally {
    setPdfGenerating(false);
  }
}

async function handlePrintContract() {
  if (!lease) return;
  // Open notice period dialog first
  setContractAction('print');
  setNoticePeriodDialogOpen(true);
}

async function executePrintContract() {
  if (!lease) return;

  setPdfGenerating(true);
  setError(null);

  try {
    const url = await generatePdfUrlFromLease(lease, noticePeriod);
    const win = window.open(url);
    if (!win) {
      throw new Error('Failed to open print window. Please check your popup blocker settings.');
    }
  } catch (error) {
    console.error('Error printing PDF:', error);
    setError(error instanceof Error ? error.message : 'Failed to print PDF. Please try again.');
  } finally {
    setPdfGenerating(false);
  }
}

const handleNoticePeriodConfirm = async () => {
  setNoticePeriodDialogOpen(false);

  // Execute the appropriate action based on what was selected
  switch (contractAction) {
    case 'preview':
      await executePreviewContract();
      break;
    case 'download':
      await executeDownloadContract();
      break;
    case 'print':
      await executePrintContract();
      break;
    case 'open':
      await executeOpenInNewTab();
      break;
  }

  setContractAction(null);
};

async function handleShareContract() {
  if (!lease) return;
  setShareDialogOpen(true);
}

const handleReceiptGroupCreated = async (receiptGroupRefNumber: string) => {
  console.log('Receipt group created:', receiptGroupRefNumber);

  // Open print options dialog
  setReceiptGroupRefForPrint(receiptGroupRefNumber);
  setPrintOptionsOpen(true);
};

async function handleWebShare() {
  if (!lease) return;

  setPdfGenerating(true);
  setError(null);
  setShareDialogOpen(false);

  try {
    const urlData = await fillLeasePdf({
      tenantName: lease.tenant?.fullName ?? '',
      email: lease.tenant?.emails?.[0] ?? '',
      phone: lease.tenant?.phoneNumbers?.[0] ?? '',
      apartmentNumber: lease.unit?.number ?? '',
      startDateISO: toISODateOnly(lease.startDate),
      monthlyRent: getMonthlyRent(lease),
      isCommercial: lease.unit?.property?.isCommercial ?? false,
      tenantAddress: {
        number: lease.tenant?.addressNumber,
        street: lease.tenant?.addressStreet,
        neighborhood: lease.tenant?.addressNeighborhood,
        municipality: lease.tenant?.addressMunicipality,
        city: lease.tenant?.addressCity
      },
      propertyCode: lease.unit?.property?.code,
      propertyContact: lease.unit?.property?.contact,
      noticePeriod: 1
    });

    const filename = `${lease.tenant?.fullName || 'Lease'}-${lease.refNumber || lease.id}.pdf`;
    const file = new File([urlData.blob], filename, { type: 'application/pdf' });

    if (navigator.share && navigator.canShare && navigator.canShare({ files: [file] })) {
      await navigator.share({
        title: `Lease Contract - ${lease.tenant?.fullName}`,
        text: `Lease contract for ${lease.tenant?.fullName} (Ref: ${lease.refNumber || lease.id})`,
        files: [file]
      });
    } else {
      // Fallback: Download the file and show info
      saveAs(urlData.blob, filename);
      alert('File downloaded. You can now attach it to an email or share it manually.');
    }
  } catch (error) {
    console.error('Error sharing PDF:', error);
    setError(error instanceof Error ? error.message : 'Failed to share PDF. Please try again.');
  } finally {
    setPdfGenerating(false);
  }
}

async function handleEmailContract() {
  if (!lease) return;

  setPdfGenerating(true);
  setError(null);
  setShareDialogOpen(false);

  try {
    const urlData = await fillLeasePdf({
      tenantName: lease.tenant?.fullName ?? '',
      email: lease.tenant?.emails?.[0] ?? '',
      phone: lease.tenant?.phoneNumbers?.[0] ?? '',
      apartmentNumber: lease.unit?.number ?? '',
      startDateISO: toISODateOnly(lease.startDate),
      monthlyRent: getMonthlyRent(lease),
      isCommercial: lease.unit?.property?.isCommercial ?? false,
      tenantAddress: {
        number: lease.tenant?.addressNumber,
        street: lease.tenant?.addressStreet,
        neighborhood: lease.tenant?.addressNeighborhood,
        municipality: lease.tenant?.addressMunicipality,
        city: lease.tenant?.addressCity
      },
      propertyCode: lease.unit?.property?.code,
      propertyContact: lease.unit?.property?.contact,
      noticePeriod: 1
    });

    const filename = `${lease.tenant?.fullName || 'Lease'}-${lease.refNumber || lease.id}.pdf`;

    // Create mailto link with tenant email
    const tenantEmail = lease.tenant?.emails?.[0] || '';
    const subject = encodeURIComponent(`Lease Contract - ${lease.refNumber || lease.id}`);
    const body = encodeURIComponent(`Dear ${lease.tenant?.fullName},\n\nPlease find your lease contract attached.\n\nLease Reference: ${lease.refNumber || lease.id}\nStart Date: ${new Date(lease.startDate).toLocaleDateString()}\nUnit: ${lease.unit?.number}\n\nBest regards`);
    const mailtoLink = `mailto:${tenantEmail}?subject=${subject}&body=${body}`;

    // Download the file and open mailto
    saveAs(urlData.blob, filename);
    window.location.href = mailtoLink;

    alert('The PDF has been downloaded. Please attach it to the email that will open.');
  } catch (error) {
    console.error('Error preparing email:', error);
    setError(error instanceof Error ? error.message : 'Failed to prepare email. Please try again.');
  } finally {
    setPdfGenerating(false);
  }
}

async function handleDownloadFromPreview() {
  if (!lease) return;

  try {
    const urlData = await fillLeasePdf({
      tenantName: lease.tenant?.fullName ?? '',
      email: lease.tenant?.emails?.[0] ?? '',
      phone: lease.tenant?.phoneNumbers?.[0] ?? '',
      apartmentNumber: lease.unit?.number ?? '',
      startDateISO: toISODateOnly(lease.startDate),
      monthlyRent: getMonthlyRent(lease),
      isCommercial: lease.unit?.property?.isCommercial ?? false,
      tenantAddress: {
        number: lease.tenant?.addressNumber,
        street: lease.tenant?.addressStreet,
        neighborhood: lease.tenant?.addressNeighborhood,
        municipality: lease.tenant?.addressMunicipality,
        city: lease.tenant?.addressCity
      },
      propertyCode: lease.unit?.property?.code,
      propertyContact: lease.unit?.property?.contact,
      noticePeriod: 1
    });
    // Use the same naming format: TenantName-RefNumber.pdf
    const filename = `${lease.tenant?.fullName || 'Lease'}-${lease.refNumber || lease.id}.pdf`;
    saveAs(urlData.blob, filename);
  } catch (error) {
    console.error('Error downloading PDF:', error);
    setError(error instanceof Error ? error.message : 'Failed to download PDF. Please try again.');
  }
}
  const fetchAppliancesConditions = async () => {
    setAppliancesLoading(true);
    try {
      const response = await DataService.get(
        `api/Lease/appliances-conditions/${leaseId}`
      );

      if (response.ok) {
        const responseData = await response.json();

        if (responseData.success && responseData.data) {
          setAppliancesConditions(responseData.data);
        }
      }
    } catch (error) {
      console.error('Error fetching appliances conditions:', error);
    } finally {
      setAppliancesLoading(false);
    }
  };

  const fetchApplianceTypes = async () => {
    try {
      const response = await DataService.get('api/appliance/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: ApplianceType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        setApplianceTypes(typesList);
      }
    } catch (error) {
      console.error('Error fetching appliance types:', error);
    }
  };

  const fetchFeeTypes = async () => {
    try {
      const response = await DataService.get('api/fee-types/all');

      if (response.ok) {
        const responseData = await response.json();
        let typesList: FeeType[] = [];

        if (responseData.success && responseData.data) {
          typesList = responseData.data;
        } else if (Array.isArray(responseData)) {
          typesList = responseData;
        }

        setFeeTypes(typesList);
      }
    } catch (error) {
      console.error('Error fetching fee types:', error);
    }
  };

  const getApplianceName = (applianceId: string): string => {
    const appliance = applianceTypes.find((type) => type.id === applianceId);
    return appliance ? appliance.name : applianceId;
  };

  const getFeeTypeName = (feeTypeId: string): string => {
    const feeType = feeTypes.find((type) => type.id === feeTypeId);
    return feeType ? feeType.name : 'N/A';
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const getDaysRemaining = (endDate: string) => {
    const end = new Date(endDate);
    const today = new Date();
    const diffTime = end.getTime() - today.getTime();
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const getStatusInfo = (status: string) => {
    return (
      LeaseStatus[status] || {
        label: status || 'Unknown',
        color: 'default' as const
      }
    );
  };

  const getInstallmentStatusInfo = (status: string) => {
    return (
      InstallmentStatus[status] || {
        label: status || 'Unknown',
        color: 'default' as const
      }
    );
  };

  const handleOpenPaymentDialog = (installment: Installment) => {
    setSelectedInstallment(installment);
    setPaymentForm({
      installmentId: installment.id,
      currencyId: installment.fee.currencyId,
      amount: installment.total,
      paidAt: new Date().toISOString().slice(0, 16),
      method: PaymentMethod.Cash
    });
    setPaymentError(null);
    setPaymentDialogOpen(true);
  };

  const handleClosePaymentDialog = () => {
    setPaymentDialogOpen(false);
    setSelectedInstallment(null);
    setPaymentError(null);
  };

  const handlePaymentSubmit = async () => {
    setPaymentLoading(true);
    setPaymentError(null);

    try {
      const payload = {
        installmentId: paymentForm.installmentId,
        currencyId: paymentForm.currencyId,
        amount: paymentForm.amount,
        paidAt: new Date(paymentForm.paidAt).toISOString(),
        method: paymentForm.method
      };

      const response = await DataService.post('api/Installment/pay', payload);

      if (response.ok) {
        // Refresh installments after successful payment
        await fetchLeaseInstallments();
        handleClosePaymentDialog();
        setError(null);
      } else {
        const errorData = await response.json();
        throw new Error(errorData.message || 'Failed to process payment');
      }
    } catch (error) {
      console.error('Error processing payment:', error);
      setPaymentError(
        error instanceof Error
          ? error.message
          : 'Failed to process payment. Please try again.'
      );
    } finally {
      setPaymentLoading(false);
    }
  };

  const canPayInstallment = (installment: Installment): boolean => {
    // Only unpaid installments can be paid (status !== 'Paid')
    return installment.status !== 'Paid' && installment.status !== 'Cancelled';
  };

  const handleSecurityDepositSuccess = async () => {
    // Refresh security deposits after adding a new one
    await fetchSecurityDeposits();
    setSecurityDepositDialogOpen(false);
  };

  const handleManualPaymentSuccess = async () => {
    // Refresh installments after making a payment
    await fetchLeaseInstallments();
    setManualPaymentDialogOpen(false);
  };

  const handleOpenUpdateTotalDialog = (installment: Installment) => {
    setSelectedInstallmentForUpdate(installment);
    setUpdateTotalDialogOpen(true);
  };

  const handleCloseUpdateTotalDialog = () => {
    setUpdateTotalDialogOpen(false);
    setSelectedInstallmentForUpdate(null);
  };

  const handleUpdateTotalSuccess = async () => {
    // Refresh installments after updating total
    await fetchLeaseInstallments();
    handleCloseUpdateTotalDialog();
  };

  const handleReceiveSecurityDeposit = async (depositId: string) => {
    try {
      const response = await DataService.get(
        `api/SecurityDeposit/receive/${depositId}`
      );

      if (response.ok) {
        // Refresh security deposits after receiving payment
        await fetchSecurityDeposits();
        setError(null);
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Failed to receive security deposit payment'
        );
      }
    } catch (error) {
      console.error('Error receiving security deposit:', error);
      setError(
        error instanceof Error
          ? error.message
          : 'Failed to receive security deposit payment. Please try again.'
      );
    }
  };

  const handleOpenRepayDialog = (deposit: SecurityDeposit) => {
    setSelectedDeposit(deposit);
    setRepayForm({
      returnAmount: 0,
      notes: ''
    });
    setRepayError(null);
    setRepayDialogOpen(true);
  };

  const handleCloseRepayDialog = () => {
    setRepayDialogOpen(false);
    setSelectedDeposit(null);
    setRepayError(null);
  };

  const handleRepaySubmit = async () => {
    if (!selectedDeposit) return;

    setRepayLoading(true);
    setRepayError(null);

    try {
      // Ensure returnAmount is a valid number
      const returnAmount = Number(repayForm.returnAmount);

      if (isNaN(returnAmount) || returnAmount <= 0) {
        throw new Error('Please enter a valid return amount');
      }

      if (returnAmount > selectedDeposit.amount) {
        throw new Error(
          'Return amount cannot exceed the original deposit amount'
        );
      }

      const payload = {
        id: selectedDeposit.id,
        returnAmount: returnAmount,
        notes: repayForm.notes || ''
      };

      console.log('Repay payload:', payload);

      const response = await DataService.post(
        'api/SecurityDeposit/repay',
        payload
      );

      if (response.ok) {
        // Refresh security deposits after successful repayment
        await fetchSecurityDeposits();
        handleCloseRepayDialog();
        setError(null);
      } else {
        const errorData = await response.json();
        throw new Error(
          errorData.message || 'Failed to process security deposit repayment'
        );
      }
    } catch (error) {
      console.error('Error processing repayment:', error);
      setRepayError(
        error instanceof Error
          ? error.message
          : 'Failed to process repayment. Please try again.'
      );
    } finally {
      setRepayLoading(false);
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          minHeight: '60vh'
        }}
      >
        <CircularProgress size={60} />
      </Box>
    );
  }

  if (error || !lease) {
    return (
      <Container maxWidth="lg" sx={{ mt: 4 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error || 'Lease not found'}
        </Alert>
        <Button
          variant="outlined"
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/leases')}
        >
          Back to Leases
        </Button>
      </Container>
    );
  }

  const statusInfo = getStatusInfo(lease.status);
  const daysRemaining = getDaysRemaining(lease.endDate);
  const totalInstallmentAmount = installments.reduce(
    (sum, inst) => sum + inst.total,
    0
  );

  return (
    <Container maxWidth="xl" sx={{ py: 1, px: 2 }}>
      {/* Page Header */}
      <PageHeader>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <IconButton
              onClick={() => navigate('/leases')}
              size="small"
              sx={{
                color: 'white',
                bgcolor: 'rgba(255, 255, 255, 0.1)',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.2)'
                }
              }}
            >
              <ArrowBackIcon />
            </IconButton>
            <Box>
              <Typography
                variant="h5"
                component="h1"
                fontWeight="bold"
                sx={{ mb: 0.5 }}
              >
                Lease Details
              </Typography>
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  flexWrap: 'wrap'
                }}
              >
                <Chip
                  label={statusInfo.label}
                  color={statusInfo.color}
                  sx={{
                    bgcolor:
                      statusInfo.color === 'success'
                        ? 'rgba(76, 175, 80, 0.2)'
                        : 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 'bold',
                    border: '1px solid rgba(255, 255, 255, 0.3)'
                  }}
                />
                {lease.refNumber && (
                  <Typography variant="body2" sx={{ opacity: 0.9 }}>
                    Ref: {lease.refNumber}
                  </Typography>
                )}
                {/* <Typography variant="body2" sx={{ opacity: 0.9 }}>
                  ID: {lease.id}
                </Typography> */}
              </Box>
            </Box>
          </Box>
          <Stack direction="row" spacing={2}>
            <Button
              variant="contained"
              startIcon={<AccountBalanceWalletIcon />}
              onClick={() => setSecurityDepositDialogOpen(true)}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Add Security Deposit
            </Button>
            <Button
              variant="contained"
              startIcon={<EditIcon />}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              Edit Lease
            </Button>

            <Button
              variant="contained"
              startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DescriptionIcon />}
              onClick={handlePreviewContract}
              disabled={pdfGenerating}
              sx={{
                bgcolor: 'rgba(255, 255, 255, 0.2)',
                color: 'white',
                '&:hover': {
                  bgcolor: 'rgba(255, 255, 255, 0.3)'
                }
              }}
            >
              {pdfGenerating ? 'Loading...' : 'Contract'}
            </Button>

            <Button
              variant="outlined"
              startIcon={pdfGenerating ? <CircularProgress size={20} color="inherit" /> : <DownloadIcon />}
              onClick={handleDownloadContract}
              disabled={pdfGenerating}
              sx={{
                color: 'white',
                borderColor: 'rgba(255, 255, 255, 0.4)',
                '&:hover': {
                  borderColor: 'rgba(255, 255, 255, 0.7)',
                  bgcolor: 'rgba(255, 255, 255, 0.1)'
                }
              }}
            >
              {pdfGenerating ? 'Loading...' : 'Download'}
            </Button>
          </Stack>
        </Box>
      </PageHeader>

      {/* Lease Information Section */}
      <Grid container spacing={1.5} sx={{ mb: 1.5 }}>
        {/* Lease Period */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <CalendarTodayIcon color="primary" />
                Lease Period
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Start Date
                  </Typography>
                  <Typography variant="h6">
                    {formatDate(lease.startDate)}
                  </Typography>
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    End Date
                  </Typography>
                  <Typography variant="h6">
                    {formatDate(lease.endDate)}
                  </Typography>
                </Box>

                {lease.checkinDate && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                      >
                        Check-In Date
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <LoginIcon
                          sx={{ fontSize: 20, color: 'primary.main' }}
                        />
                        <Typography variant="h6">
                          {formatDate(lease.checkinDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {lease.checkoutDate && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                      >
                        Check-Out Date
                      </Typography>
                      <Box
                        sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}
                      >
                        <LogoutIcon
                          sx={{ fontSize: 20, color: 'warning.main' }}
                        />
                        <Typography variant="h6">
                          {formatDate(lease.checkoutDate)}
                        </Typography>
                      </Box>
                    </Box>
                  </>
                )}

                {daysRemaining > 0 && (
                  <>
                    <Divider />
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                      >
                        Days Remaining
                      </Typography>
                      <Chip
                        label={`${daysRemaining} days`}
                        color={daysRemaining < 30 ? 'warning' : 'success'}
                        sx={{ fontWeight: 'bold', fontSize: '0.9rem' }}
                      />
                    </Box>
                  </>
                )}

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Payment Date
                  </Typography>
                  <Typography variant="h6">
                    Day {lease.paymentDateOn} of each month
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>

        {/* Tenant & Unit Information */}
        <Grid item xs={12} md={6}>
          <SectionCard>
            <CardContent sx={{ p: 3 }}>
              <Typography
                variant="h5"
                gutterBottom
                sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}
              >
                <PersonIcon color="primary" />
                Tenant & Unit
              </Typography>

              <Stack spacing={2}>
                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Tenant Name
                  </Typography>
                  <Typography variant="h6">{lease.tenant.fullName}</Typography>
                  {lease.tenant.emails.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {lease.tenant.emails[0]}
                    </Typography>
                  )}
                  {lease.tenant.phoneNumbers.length > 0 && (
                    <Typography variant="body2" color="text.secondary">
                      {lease.tenant.phoneNumbers[0]}
                    </Typography>
                  )}
                </Box>

                <Divider />

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Unit
                  </Typography>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <HomeIcon color="primary" />
                    <Typography variant="h6">
                      Unit {lease.unit.number}
                    </Typography>
                  </Box>
                  <Typography variant="body2" color="text.secondary">
                    {lease.unit.rooms} rooms • {lease.unit.bathrooms} bathrooms
                    • {lease.unit.areaM2} m² • Floor {lease.unit.floor}
                  </Typography>
                </Box>

                <Divider />

                {lease.unit.property && (
                  <>
                    <Box>
                      <Typography
                        variant="subtitle2"
                        color="text.secondary"
                        gutterBottom
                        sx={{ fontWeight: 'bold' }}
                      >
                        Property
                      </Typography>
                      <Typography variant="body1">
                        <strong>Code:</strong> {lease.unit.property.code}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Type:</strong> {lease.unit.property.propertyType}
                      </Typography>
                      <Typography variant="body1">
                        <strong>Category:</strong>{' '}
                        <Chip
                          label={
                            lease.unit.property.isCommercial
                              ? 'Commercial'
                              : 'Residential'
                          }
                          size="small"
                          color={
                            lease.unit.property.isCommercial
                              ? 'primary'
                              : 'success'
                          }
                          sx={{ fontWeight: 'bold' }}
                        />
                      </Typography>
                      {lease.unit.property.contact && (
                        <Typography variant="body1">
                          <strong>Contact:</strong> {lease.unit.property.contact}
                        </Typography>
                      )}
                    </Box>
                    <Divider />
                  </>
                )}

                <Box>
                  <Typography
                    variant="subtitle2"
                    color="text.secondary"
                    gutterBottom
                    sx={{ fontWeight: 'bold' }}
                  >
                    Lease Type & Currency
                  </Typography>
                  <Typography variant="body1">
                    <strong>Type:</strong> {lease.leaseType.toUpperCase()}
                  </Typography>
                  <Typography variant="body1">
                    <strong>Currency:</strong> {lease.currency}
                  </Typography>
                </Box>
              </Stack>
            </CardContent>
          </SectionCard>
        </Grid>
      </Grid>

      {/* Fees Section */}
      {lease.fees && lease.fees.length > 0 && (
        <SectionCard sx={{ mb: 1.5 }}>
          <CardContent sx={{ p: 1.5 }}>
            <Typography
              variant="subtitle1"
              gutterBottom
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                mb: 1.5,
                fontWeight: 'bold'
              }}
            >
              <AttachMoneyIcon color="primary" />
              Configured Fees ({lease.fees.length})
            </Typography>

            <Grid container spacing={1}>
              {lease.fees.map((fee, index) => (
                <Grid item xs={12} sm={6} md={4} key={fee.id}>
                  <Paper sx={{ p: 1, bgcolor: 'grey.50' }}>
                    <Typography
                      variant="subtitle1"
                      fontWeight="bold"
                      gutterBottom
                    >
                      {fee.type}
                    </Typography>
                    <Typography variant="h5" color="success.main" gutterBottom>
                      {fee.currency} {fee.value.toLocaleString()}
                    </Typography>
                    <Chip
                      label={fee.billingCycle}
                      size="small"
                      color="primary"
                      variant="outlined"
                      sx={{ mb: 1 }}
                    />
                    {fee.notes && (
                      <Typography
                        variant="caption"
                        display="block"
                        color="text.secondary"
                      >
                        {fee.notes}
                      </Typography>
                    )}
                  </Paper>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </SectionCard>
      )}

      {/* Security Deposits Section */}
      <SectionCard sx={{ mb: 1.5 }}>
        <CardContent sx={{ p: 1.5 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              mb: 1.5
            }}
          >
            <Typography
              variant="subtitle1"
              sx={{
                display: 'flex',
                alignItems: 'center',
                gap: 1,
                fontWeight: 'bold'
              }}
            >
              <AccountBalanceWalletIcon color="primary" />
              Security Deposits ({securityDeposits.length})
            </Typography>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
              {securityDeposits.length > 0 && (
                <Box sx={{ textAlign: 'right' }}>
                  <Typography
                    variant="body2"
                    color="text.secondary"
                    gutterBottom
                  >
                    Total Security Deposits
                  </Typography>
                  <Typography
                    variant="h5"
                    fontWeight="bold"
                    color="success.main"
                  >
                    {lease.currency}{' '}
                    {securityDeposits
                      .reduce((sum, dep) => sum + dep.amount, 0)
                      .toLocaleString()}
                  </Typography>
                </Box>
              )}
              <Button
                variant="contained"
                size="small"
                startIcon={<AccountBalanceWalletIcon />}
                onClick={() => setSecurityDepositDialogOpen(true)}
                sx={{ fontWeight: 'bold' }}
              >
                Add Deposit
              </Button>
            </Box>
          </Box>

          {securityDepositsLoading ? (
            <Box
              sx={{
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                minHeight: '150px'
              }}
            >
              <CircularProgress size={40} />
            </Box>
          ) : securityDeposits.length === 0 ? (
            <Box
              sx={{
                p: 2,
                textAlign: 'center',
                bgcolor: 'grey.50',
                borderRadius: 2
              }}
            >
              <AccountBalanceWalletIcon
                sx={{ fontSize: 40, color: 'grey.300', mb: 1 }}
              />
              <Typography
                variant="subtitle2"
                color="text.secondary"
                gutterBottom
              >
                No security deposits found
              </Typography>
              <Typography variant="caption" color="text.secondary">
                This lease doesn't have any security deposits yet.
              </Typography>
            </Box>
          ) : (
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow sx={{ bgcolor: 'grey.50' }}>
                    <TableCell>
                      <strong>#</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Ref Number</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Amount</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Return Amount</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Status</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Payment Date</strong>
                    </TableCell>
                    <TableCell>
                      <strong>Notes</strong>
                    </TableCell>
                    <TableCell align="center">
                      <strong>Actions</strong>
                    </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {securityDeposits.map((deposit, index) => (
                    <TableRow key={deposit.id} hover>
                      <TableCell>
                        <Typography variant="body2" fontWeight="600">
                          {index + 1}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          fontWeight="600"
                          color="primary"
                        >
                          {deposit.refNumber}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <AccountBalanceWalletIcon
                            sx={{ fontSize: 16, color: 'success.main' }}
                          />
                          <Typography
                            variant="body1"
                            fontWeight="600"
                            color="success.main"
                          >
                            {lease.currency} {deposit.amount.toLocaleString()}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography
                          variant="body2"
                          color={
                            deposit.returnAmount > 0
                              ? 'error.main'
                              : 'text.secondary'
                          }
                        >
                          {deposit.returnAmount > 0
                            ? `${
                                lease.currency
                              } ${deposit.returnAmount.toLocaleString()}`
                            : '-'}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        <Chip
                          label={deposit.status}
                          size="small"
                          color={
                            deposit.status === 'Pending'
                              ? 'warning'
                              : deposit.status === 'Returned'
                              ? 'success'
                              : 'default'
                          }
                          sx={{ fontWeight: 'bold' }}
                        />
                      </TableCell>
                      <TableCell>
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: 0.5
                          }}
                        >
                          <CalendarTodayIcon
                            sx={{ fontSize: 16, color: 'text.secondary' }}
                          />
                          <Typography variant="body2">
                            {formatDate(deposit.paymentDate)}
                          </Typography>
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Typography variant="caption" color="text.secondary">
                          {deposit.notes || '-'}
                        </Typography>
                      </TableCell>
                      <TableCell align="center">
                        {deposit.status === 'Pending' ? (
                          <Button
                            variant="contained"
                            color="success"
                            size="small"
                            startIcon={<PaymentIcon />}
                            onClick={() =>
                              handleReceiveSecurityDeposit(deposit.id)
                            }
                            sx={{ fontWeight: 'bold' }}
                          >
                            Receive Payment
                          </Button>
                        ) : deposit.status === 'Received' ? (
                          <Button
                            variant="contained"
                            color="primary"
                            size="small"
                            startIcon={<AccountBalanceWalletIcon />}
                            onClick={() => handleOpenRepayDialog(deposit)}
                            sx={{ fontWeight: 'bold' }}
                          >
                            Repay
                          </Button>
                        ) : (
                          <Typography variant="caption" color="text.secondary">
                            -
                          </Typography>
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </SectionCard>

      {/* Check-In/Check-Out Appliances Section */}
      {appliancesConditions &&
        (appliancesConditions.checkinAppliancesConditions.length > 0 ||
          appliancesConditions.checkoutAppliancesConditions.length > 0) && (
          <SectionCard sx={{ mb: 1.5 }}>
            <CardContent sx={{ p: 1.5 }}>
              <Typography
                variant="subtitle1"
                gutterBottom
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  mb: 1.5,
                  fontWeight: 'bold'
                }}
              >
                <DevicesIcon color="primary" />
                Check-In Check-Out
              </Typography>

              {appliancesLoading ? (
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    minHeight: '150px'
                  }}
                >
                  <CircularProgress size={40} />
                </Box>
              ) : (
                <Grid container spacing={1.5}>
                  {/* Check-In Section */}
                  {appliancesConditions.checkinAppliancesConditions.length >
                    0 && (
                    <Grid item xs={12} md={6}>
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: 'rgba(33, 150, 243, 0.05)',
                          border: '2px solid rgba(33, 150, 243, 0.2)'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <LoginIcon color="primary" />
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color="primary"
                            >
                              Check-In Appliances (
                              {
                                appliancesConditions.checkinAppliancesConditions
                                  .length
                              }
                              )
                            </Typography>
                          </Box>
                          {lease.checkinDate && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: 14, color: 'primary.main' }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                color="primary"
                              >
                                {formatDate(lease.checkinDate)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow
                                sx={{ bgcolor: 'rgba(33, 150, 243, 0.1)' }}
                              >
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  #
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  Appliance
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  Condition
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  Notes
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {appliancesConditions.checkinAppliancesConditions.map(
                                (appliance, index) => (
                                  <TableRow key={index} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {getApplianceName(
                                          appliance.applianceId
                                        )}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={appliance.conditionStatus}
                                        size="small"
                                        color={
                                          appliance.conditionStatus ===
                                            'Excellent' ||
                                          appliance.conditionStatus === 'Good'
                                            ? 'success'
                                            : appliance.conditionStatus ===
                                              'Fair'
                                            ? 'warning'
                                            : 'error'
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {appliance.notes || '-'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  )}

                  {/* Check-Out Section */}
                  {appliancesConditions.checkoutAppliancesConditions.length >
                    0 && (
                    <Grid item xs={12} md={6}>
                      <Paper
                        sx={{
                          p: 1.5,
                          bgcolor: 'rgba(255, 152, 0, 0.05)',
                          border: '2px solid rgba(255, 152, 0, 0.2)'
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'space-between',
                            mb: 1
                          }}
                        >
                          <Box
                            sx={{
                              display: 'flex',
                              alignItems: 'center',
                              gap: 1
                            }}
                          >
                            <LogoutIcon color="warning" />
                            <Typography
                              variant="subtitle2"
                              fontWeight="bold"
                              color="warning.main"
                            >
                              Check-Out Appliances (
                              {
                                appliancesConditions
                                  .checkoutAppliancesConditions.length
                              }
                              )
                            </Typography>
                          </Box>
                          {lease.checkoutDate && (
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: 14, color: 'warning.main' }}
                              />
                              <Typography
                                variant="caption"
                                fontWeight="bold"
                                color="warning.main"
                              >
                                {formatDate(lease.checkoutDate)}
                              </Typography>
                            </Box>
                          )}
                        </Box>
                        <TableContainer>
                          <Table size="small">
                            <TableHead>
                              <TableRow
                                sx={{ bgcolor: 'rgba(255, 152, 0, 0.1)' }}
                              >
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  #
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  Appliance
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  Condition
                                </TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>
                                  Notes
                                </TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {appliancesConditions.checkoutAppliancesConditions.map(
                                (appliance, index) => (
                                  <TableRow key={index} hover>
                                    <TableCell>{index + 1}</TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="body2"
                                        fontWeight="medium"
                                      >
                                        {getApplianceName(
                                          appliance.applianceId
                                        )}
                                      </Typography>
                                    </TableCell>
                                    <TableCell>
                                      <Chip
                                        label={appliance.conditionStatus}
                                        size="small"
                                        color={
                                          appliance.conditionStatus ===
                                            'Excellent' ||
                                          appliance.conditionStatus === 'Good'
                                            ? 'success'
                                            : appliance.conditionStatus ===
                                              'Fair'
                                            ? 'warning'
                                            : 'error'
                                        }
                                      />
                                    </TableCell>
                                    <TableCell>
                                      <Typography
                                        variant="caption"
                                        color="text.secondary"
                                      >
                                        {appliance.notes || '-'}
                                      </Typography>
                                    </TableCell>
                                  </TableRow>
                                )
                              )}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Paper>
                    </Grid>
                  )}
                </Grid>
              )}
            </CardContent>
          </SectionCard>
        )}

      {/* Installments Section */}
      <Box>
        <Paper sx={{ borderRadius: 2, overflow: 'hidden' }}>
          <Box sx={{ bgcolor: 'primary.main', color: 'white', p: 1.5 }}>
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between'
              }}
            >
              <Typography
                variant="h6"
                fontWeight="bold"
                sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
              >
                <ReceiptIcon sx={{ fontSize: 32 }} />
                Installments ({installments.length})
              </Typography>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                <Box sx={{ textAlign: 'right' }}>
                  <Typography variant="caption" sx={{ opacity: 0.9 }}>
                    Total Amount
                  </Typography>
                  <Typography variant="h6" fontWeight="bold">
                    {lease.currency} {totalInstallmentAmount.toLocaleString()}
                  </Typography>
                </Box>
                <Button
                  variant="contained"
                  size="small"
                  startIcon={<PaymentIcon />}
                  onClick={() => setManualPaymentDialogOpen(true)}
                  sx={{
                    bgcolor: 'rgba(255, 255, 255, 0.2)',
                    color: 'white',
                    fontWeight: 'bold',
                    '&:hover': {
                      bgcolor: 'rgba(255, 255, 255, 0.3)'
                    }
                  }}
                >
                  Add Receipt
                </Button>
              </Box>
            </Box>
          </Box>

          <Box sx={{ p: 1.5 }}>
            {installmentsLoading ? (
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  minHeight: '300px'
                }}
              >
                <CircularProgress size={60} />
              </Box>
            ) : installments.length === 0 ? (
              <Box
                sx={{
                  p: 3,
                  textAlign: 'center',
                  bgcolor: 'grey.50',
                  borderRadius: 2
                }}
              >
                <ReceiptIcon sx={{ fontSize: 60, color: 'grey.300', mb: 1 }} />
                <Typography
                  variant="subtitle1"
                  color="text.secondary"
                  gutterBottom
                >
                  No installments found
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  This lease doesn't have any installments yet.
                </Typography>
              </Box>
            ) : (
              <TableContainer>
                <Table>
                  <TableHead>
                    <TableRow sx={{ bgcolor: 'grey.50' }}>
                      <TableCell>
                        <strong>#</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Issued Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Due Date</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Fee Type</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Total Amount</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Paid Amount</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Remaining</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Status</strong>
                      </TableCell>
                      <TableCell>
                        <strong>Receipts</strong>
                      </TableCell>
                      <TableCell align="center">
                        <strong>Actions</strong>
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {installments.map((installment, index) => {
                      const statusInfo = getInstallmentStatusInfo(
                        installment.status
                      );
                      // Group payments by receipt group
                      const paymentsByGroup = installment.payments?.reduce((acc, payment) => {
                        const groupRef = payment.receiptGroupReference;
                        if (!acc[groupRef]) {
                          acc[groupRef] = {
                            receiptGroupId: payment.receiptGroupId,
                            receiptGroupReference: groupRef,
                            payments: [],
                            totalAmount: 0,
                            latestPaymentDate: payment.paidAt
                          };
                        }
                        acc[groupRef].payments.push(payment);
                        acc[groupRef].totalAmount += payment.amountPaid;
                        if (new Date(payment.paidAt) > new Date(acc[groupRef].latestPaymentDate)) {
                          acc[groupRef].latestPaymentDate = payment.paidAt;
                        }
                        return acc;
                      }, {} as Record<string, { receiptGroupId: string; receiptGroupReference: string; payments: Payment[]; totalAmount: number; latestPaymentDate: string }>);

                      return (
                        <TableRow key={installment.id} hover>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600">
                              {index + 1}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                              />
                              <Typography variant="body2">
                                {formatDate(installment.issuedAt)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <CalendarTodayIcon
                                sx={{ fontSize: 16, color: 'text.secondary' }}
                              />
                              <Typography variant="body2">
                                {formatDate(installment.dueDate)}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2">
                              {getFeeTypeName(installment.fee.feeTypeId)}
                            </Typography>
                            <Typography variant="caption" color="text.secondary" display="block">
                              {installment.fee.billingCycle}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Box
                              sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: 0.5
                              }}
                            >
                              <AttachMoneyIcon
                                sx={{ fontSize: 16, color: 'success.main' }}
                              />
                              <Typography
                                variant="body2"
                                fontWeight="600"
                                color="success.main"
                              >
                                {installment.currency || lease.currency} {installment.total.toLocaleString()}
                              </Typography>
                            </Box>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600" color={installment.paidAmount > 0 ? "success.main" : "text.secondary"}>
                              {installment.currency || lease.currency} {(installment.paidAmount || 0).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Typography variant="body2" fontWeight="600" color={installment.remainingAmount > 0 ? "warning.main" : "success.main"}>
                              {installment.currency || lease.currency} {(installment.remainingAmount || installment.total).toLocaleString()}
                            </Typography>
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={statusInfo.label}
                              color={statusInfo.color}
                              size="small"
                              sx={{ fontWeight: 'bold' }}
                            />
                          </TableCell>
                          <TableCell>
                            {installment.payments && installment.payments.length > 0 ? (
                              <Box>
                                {paymentsByGroup && Object.values(paymentsByGroup).map((group, idx) => (
                                  <Box key={idx} sx={{ mb: 1, p: 1, bgcolor: 'grey.50', borderRadius: 1, border: '1px solid', borderColor: 'grey.200' }}>
                                    <Typography variant="caption" fontWeight="bold" color="primary">
                                      {group.receiptGroupReference}
                                    </Typography>
                                    <Typography variant="caption" display="block" color="text.secondary">
                                      Paid: {formatDateTime(group.latestPaymentDate)}
                                    </Typography>
                                    <Typography variant="caption" display="block" fontWeight="600" color="success.main">
                                      Total: {installment.currency || lease.currency} {group.totalAmount.toLocaleString()}
                                    </Typography>
                                    {/* <Typography variant="caption" display="block" color="text.secondary">
                                      ({group.payments.length} receipt{group.payments.length > 1 ? 's' : ''})
                                    </Typography> */}
                                  </Box>
                                ))}
                              </Box>
                            ) : (
                              <Typography variant="caption" color="text.secondary">
                                No payments yet
                              </Typography>
                            )}
                          </TableCell>
                          <TableCell align="center">
                            {installment.status !== 'Paid' &&
                            installment.status !== 'Completed' ? (
                              <Tooltip title="Update Total">
                                <IconButton
                                  size="small"
                                  color="primary"
                                  onClick={() =>
                                    handleOpenUpdateTotalDialog(installment)
                                  }
                                >
                                  <AttachMoneyIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            ) : (
                              <Typography
                                variant="caption"
                                color="text.secondary"
                              >
                                -
                              </Typography>
                            )}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            )}
          </Box>
        </Paper>
      </Box>

      {/* Payment Dialog */}
      <Dialog
        open={paymentDialogOpen}
        onClose={handleClosePaymentDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ bgcolor: 'success.main', color: 'white', fontWeight: 'bold' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <PaymentIcon />
            Process Payment
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {paymentError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {paymentError}
            </Alert>
          )}

          {selectedInstallment && (
            <Box>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Installment Details
                </Typography>
                <Typography variant="body1">
                  <strong>Fee Type:</strong>{' '}
                  {selectedInstallment.fee.type || 'N/A'}
                </Typography>
                <Typography variant="body1">
                  <strong>Amount Due:</strong>{' '}
                  {selectedInstallment.total.toLocaleString()}{' '}
                  {selectedInstallment.fee.currency}
                </Typography>
                <Typography variant="body1">
                  <strong>Due Date:</strong>{' '}
                  {formatDate(selectedInstallment.dueDate)}
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payment Amount"
                    type="number"
                    value={paymentForm.amount}
                    onChange={(e) =>
                      setPaymentForm({
                        ...paymentForm,
                        amount: parseFloat(e.target.value) || 0
                      })
                    }
                    inputProps={{ min: 0, step: 0.01 }}
                    required
                  />
                </Grid>

                <Grid item xs={12}>
                  <FormControl fullWidth required>
                    <InputLabel>Payment Method</InputLabel>
                    <Select
                      value={paymentForm.method}
                      onChange={(e) =>
                        setPaymentForm({
                          ...paymentForm,
                          method: e.target.value as PaymentMethod
                        })
                      }
                      label="Payment Method"
                    >
                      {Object.entries(PaymentMethodLabels).map(
                        ([value, label]) => (
                          <MenuItem key={value} value={parseInt(value)}>
                            {label}
                          </MenuItem>
                        )
                      )}
                    </Select>
                  </FormControl>
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Payment Date & Time"
                    type="datetime-local"
                    value={paymentForm.paidAt}
                    onChange={(e) =>
                      setPaymentForm({ ...paymentForm, paidAt: e.target.value })
                    }
                    InputLabelProps={{
                      shrink: true
                    }}
                    required
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleClosePaymentDialog}
            disabled={paymentLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handlePaymentSubmit}
            variant="contained"
            color="success"
            disabled={
              paymentLoading || !paymentForm.amount || paymentForm.amount <= 0
            }
            startIcon={
              paymentLoading ? (
                <CircularProgress size={20} />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {paymentLoading ? 'Processing...' : 'Confirm Payment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Security Deposit Dialog */}
      {leaseId && (
        <SecurityDepositDialog
          open={securityDepositDialogOpen}
          onClose={() => setSecurityDepositDialogOpen(false)}
          onSuccess={handleSecurityDepositSuccess}
          leaseId={leaseId}
        />
      )}

      {/* Notice Period Dialog */}
      <Dialog
        open={noticePeriodDialogOpen}
        onClose={() => setNoticePeriodDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle
          sx={{
            bgcolor: 'primary.main',
            color: 'white',
            fontWeight: 'bold',
            py: 2
          }}
        >
          Select Notice Period
        </DialogTitle>
        <DialogContent sx={{ pt: 3, pb: 2 }}>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Please select the notice period (in months) for this contract:
          </Typography>
          <FormControl fullWidth>
            <InputLabel>Notice Period</InputLabel>
            <Select
              value={noticePeriod}
              onChange={(e) => setNoticePeriod(e.target.value as 1 | 3)}
              label="Notice Period"
            >
              <MenuItem value={1}>1 Month</MenuItem>
              <MenuItem value={3}>3 Months</MenuItem>
            </Select>
          </FormControl>
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button
            onClick={() => setNoticePeriodDialogOpen(false)}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleNoticePeriodConfirm}
            variant="contained"
            color="primary"
          >
            Continue
          </Button>
        </DialogActions>
      </Dialog>

      {/* Manual Payment Dialog */}
      <ManualPaymentDialog
        open={manualPaymentDialogOpen}
        onClose={() => setManualPaymentDialogOpen(false)}
        onSuccess={handleManualPaymentSuccess}
        onReceiptCreated={handleReceiptGroupCreated}
        lease={lease}
      />

      {/* Print Options Dialog */}
      <PrintOptionsDialog
        open={printOptionsOpen}
        onClose={() => {
          setPrintOptionsOpen(false);
          setReceiptGroupRefForPrint(null);
        }}
        receiptGroupRefNumber={receiptGroupRefForPrint}
      />

      {/* Update Installment Total Dialog */}
      <UpdateInstallmentTotalDialog
        open={updateTotalDialogOpen}
        onClose={handleCloseUpdateTotalDialog}
        onSuccess={handleUpdateTotalSuccess}
        installment={selectedInstallmentForUpdate}
      />

      {/* Repay Security Deposit Dialog */}
      <Dialog
        open={repayDialogOpen}
        onClose={handleCloseRepayDialog}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle
          sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <AccountBalanceWalletIcon />
            Repay Security Deposit
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          {repayError && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {repayError}
            </Alert>
          )}

          {selectedDeposit && (
            <Box>
              <Paper sx={{ p: 2, mb: 3, bgcolor: 'grey.50' }}>
                <Typography
                  variant="subtitle2"
                  color="text.secondary"
                  gutterBottom
                >
                  Security Deposit Details
                </Typography>
                <Typography variant="body1">
                  <strong>Ref Number:</strong> {selectedDeposit.refNumber}
                </Typography>
                <Typography variant="body1">
                  <strong>Original Amount:</strong> {lease?.currency}{' '}
                  {selectedDeposit.amount.toLocaleString()}
                </Typography>
                <Typography variant="body1">
                  <strong>Payment Date:</strong>{' '}
                  {formatDate(selectedDeposit.paymentDate)}
                </Typography>
              </Paper>

              <Grid container spacing={2}>
                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Return Amount"
                    type="number"
                    value={repayForm.returnAmount}
                    onChange={(e) =>
                      setRepayForm({
                        ...repayForm,
                        returnAmount: parseFloat(e.target.value) || 0
                      })
                    }
                    inputProps={{
                      min: 0,
                      step: 0.01,
                      max: selectedDeposit.amount
                    }}
                    required
                    helperText={`Maximum: ${
                      lease?.currency
                    } ${selectedDeposit.amount.toLocaleString()}`}
                  />
                </Grid>

                <Grid item xs={12}>
                  <TextField
                    fullWidth
                    label="Notes"
                    multiline
                    rows={3}
                    value={repayForm.notes}
                    onChange={(e) =>
                      setRepayForm({ ...repayForm, notes: e.target.value })
                    }
                    placeholder="Add any notes about the repayment..."
                  />
                </Grid>
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button
            onClick={handleCloseRepayDialog}
            disabled={repayLoading}
            variant="outlined"
          >
            Cancel
          </Button>
          <Button
            onClick={handleRepaySubmit}
            variant="contained"
            color="primary"
            disabled={
              repayLoading ||
              !repayForm.returnAmount ||
              repayForm.returnAmount <= 0
            }
            startIcon={
              repayLoading ? (
                <CircularProgress size={20} />
              ) : (
                <CheckCircleIcon />
              )
            }
          >
            {repayLoading ? 'Processing...' : 'Confirm Repayment'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* PDF Preview Dialog */}
      <Dialog open={previewOpen} onClose={() => setPreviewOpen(false)} maxWidth="lg" fullWidth>
        <DialogTitle>
          {lease ? `${lease.tenant?.fullName || 'Lease Contract'} - ${lease.refNumber || lease.id}` : 'Lease Contract Preview'}
        </DialogTitle>
        <DialogContent dividers sx={{ height: '80vh', p: 0 }}>
          {pdfUrl ? (
            <iframe
              title="Lease PDF Preview"
              src={pdfUrl}
              style={{ border: 'none', width: '100%', height: '100%' }}
            />
          ) : (
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%' }}>
              <CircularProgress />
            </Box>
          )}
        </DialogContent>
        <DialogActions sx={{ p: 2, justifyContent: 'space-between' }}>
          <Button onClick={() => setPreviewOpen(false)} variant="outlined">Close</Button>
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Button
              onClick={handleOpenInNewTab}
              variant="outlined"
              startIcon={<VisibilityIcon />}
            >
              Open in New Tab
            </Button>
            <Button
              onClick={handleDownloadFromPreview}
              variant="outlined"
              startIcon={<DownloadIcon />}
            >
              Download
            </Button>
            <Button
              onClick={handleShareContract}
              variant="contained"
              startIcon={<ShareIcon />}
            >
              Share
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      {/* Share Options Dialog */}
      <Dialog
        open={shareDialogOpen}
        onClose={() => setShareDialogOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle sx={{ bgcolor: 'primary.main', color: 'white', fontWeight: 'bold' }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
            <ShareIcon />
            Share Contract
          </Box>
        </DialogTitle>
        <DialogContent sx={{ mt: 3 }}>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={pdfGenerating ? <CircularProgress size={20} /> : <ShareIcon />}
                onClick={handleWebShare}
                disabled={pdfGenerating}
                sx={{ py: 2, justifyContent: 'flex-start' }}
              >
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Share via Apps
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Share to WhatsApp, Messenger, etc.
                  </Typography>
                </Box>
              </Button>
            </Grid>

            <Grid item xs={12}>
              <Button
                fullWidth
                variant="outlined"
                size="large"
                startIcon={pdfGenerating ? <CircularProgress size={20} /> : <EmailIcon />}
                onClick={handleEmailContract}
                disabled={pdfGenerating}
                sx={{ py: 2, justifyContent: 'flex-start' }}
              >
                <Box sx={{ flex: 1, textAlign: 'left' }}>
                  <Typography variant="subtitle1" fontWeight="bold">
                    Share via Email
                  </Typography>
                  <Typography variant="caption" color="text.secondary">
                    Send to {lease?.tenant?.emails?.[0] || 'tenant'}
                  </Typography>
                </Box>
              </Button>
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ p: 3, pt: 2 }}>
          <Button onClick={() => setShareDialogOpen(false)} variant="outlined">
            Cancel
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default LeaseDetailsPage;
